import { useEffect, useState } from "react";
import { Building2, Loader2, Save } from "lucide-react";
import { useAuth } from "@/components/providers/auth-provider";
import {
  useEmpresaProfile,
  useUpdateEmpresaProfile,
  type EmpresaProfileUpdate,
} from "@/hooks/use-empresa-profile";

// Mejora "Perfil empresa" (changelog cliente): página admin para
// describir los recursos de la empresa. Lo que se rellene aquí se
// inyecta automáticamente en los agentes (Comparador, Evaluador,
// Riesgos, Contexto) y se ofrece al generador de propuestas.

interface FieldDef {
  key: keyof EmpresaProfileUpdate;
  label: string;
  description: string;
  placeholder: string;
}

const FIELDS: FieldDef[] = [
  {
    key: "descripcion_general",
    label: "Descripción general",
    description:
      "Qué hace la empresa, valores, propuesta de valor, sectores en los que opera.",
    placeholder:
      "Ej.: Meridiana es una entidad sin ánimo de lucro especializada en intervención socioeducativa con menores en conflicto con la ley…",
  },
  {
    key: "capacidades_clave",
    label: "Capacidades clave",
    description:
      "Áreas de especialización y servicios que sabéis prestar bien.",
    placeholder:
      "Ej.: Centros de internamiento, medidas judiciales en medio abierto, formación, prevención del fracaso escolar…",
  },
  {
    key: "equipo",
    label: "Equipo y recursos humanos",
    description:
      "Tamaño, perfiles profesionales, ratios típicos, cultura del equipo.",
    placeholder:
      "Ej.: 120 educadores sociales, 12 psicólogos, 4 directores de centro, ratio educador:menor 1:5…",
  },
  {
    key: "certificaciones",
    label: "Certificaciones y acreditaciones",
    description:
      "ISO, sellos de calidad, registros oficiales, acreditaciones autonómicas.",
    placeholder:
      "Ej.: ISO 9001:2015, ISO 14001, Sello EFQM 400+, inscrita en el registro de Entidades de Iniciativa Social de Andalucía…",
  },
  {
    key: "instalaciones_recursos",
    label: "Instalaciones y recursos materiales",
    description:
      "Centros propios, equipamiento, infraestructura tecnológica.",
    placeholder:
      "Ej.: 4 centros de internamiento (Málaga, El Molino, Sierra Morena, Genil), 3 oficinas territoriales, sistema de gestión documental propio…",
  },
  {
    key: "metodologia",
    label: "Metodología y enfoque de trabajo",
    description: "Modelo de intervención, fases de un proyecto típico.",
    placeholder:
      "Ej.: Modelo de intervención sistémico-relacional, planes individualizados, evaluación continua con indicadores SMART…",
  },
  {
    key: "proyectos_destacados",
    label: "Proyectos destacados y experiencia",
    description:
      "Concursos ganados, casos de éxito, hitos. La IA los usará para posicionar las propuestas.",
    placeholder:
      "Ej.: Adjudicatarios del CIMI Genil 2020-2025 (Granada), centro de día El Molino 2018-actualidad, premio Andalucía Solidaria 2022…",
  },
];

export default function PerfilEmpresaPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const { data: profile, isLoading, isError } = useEmpresaProfile();
  const updateMutation = useUpdateEmpresaProfile();

  const [draft, setDraft] = useState<EmpresaProfileUpdate>({});

  // Sincroniza el formulario con los datos del servidor al primer fetch.
  useEffect(() => {
    if (!profile) return;
    setDraft({
      descripcion_general: profile.descripcion_general ?? "",
      capacidades_clave: profile.capacidades_clave ?? "",
      equipo: profile.equipo ?? "",
      certificaciones: profile.certificaciones ?? "",
      instalaciones_recursos: profile.instalaciones_recursos ?? "",
      metodologia: profile.metodologia ?? "",
      proyectos_destacados: profile.proyectos_destacados ?? "",
    });
  }, [profile]);

  const update = (key: keyof EmpresaProfileUpdate, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  if (authLoading) {
    return (
      <div className="p-6">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-start gap-3 mb-6 flex-wrap">
        <div className="flex items-start gap-2">
          <Building2 className="w-6 h-6 text-blue-600 mt-0.5" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Perfil de empresa
            </h1>
            <p className="text-sm text-gray-500 max-w-2xl mt-1">
              Descripción detallada de los recursos, capacidades y credenciales
              de la empresa. La IA inyecta este perfil automáticamente como
              contexto al ejecutar los agentes (Comparador, Evaluador,
              Riesgos, Contexto) y al generar propuestas.
            </p>
          </div>
        </div>
      </div>

      {!isAdmin && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
          La edición del perfil requiere permisos de administrador. Si
          necesitas cambios, contacta con tu administrador.
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando perfil…
        </div>
      )}

      {isError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          Error cargando el perfil. Recarga la página.
        </div>
      )}

      {!isLoading && !isError && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate(draft);
          }}
          className="space-y-6"
        >
          {FIELDS.map((f) => (
            <div key={String(f.key)}>
              <label className="block text-sm font-semibold text-gray-900 mb-1">
                {f.label}
              </label>
              <p className="text-xs text-gray-500 mb-2">{f.description}</p>
              <textarea
                value={(draft[f.key] as string) ?? ""}
                onChange={(e) => update(f.key, e.target.value)}
                placeholder={f.placeholder}
                rows={4}
                disabled={!isAdmin || updateMutation.isPending}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
          ))}

          {isAdmin && (
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar perfil
              </button>
              {profile?.exists && profile.updated_at && (
                <span className="text-xs text-gray-400">
                  Última actualización:{" "}
                  {new Date(profile.updated_at).toLocaleDateString("es-ES", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              )}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
